/**
 * SF-3180 Task 4 — inline validation helpers for the SSO config form.
 *
 * Mirrors the server-side validators in sf-auth-service
 * `src/routes/ssoConfig.ts` (SF-3404) so the admin UI can block submit
 * when a field is clearly wrong, instead of round-tripping a 400.
 *
 * The backend remains authoritative — these helpers are the pre-flight
 * check; the PATCH handler in Tab still surfaces any server-side error
 * message when the save fails (e.g. domain-conflict 409 or a regex
 * rejected by the backend's stricter ReDoS heuristic).
 *
 * Keep messages operator-friendly — Erol-test: would a non-engineer
 * know what to fix from this message?
 */
import type { SsoProvider } from './types';

/** RFC-1035-ish domain shape — matches normalizeDomains in the auth service. */
const DOMAIN_RE =
  /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;

export interface FieldErrors {
  name?: string;
  clientId?: string;
  clientSecret?: string;
  discoveryUrl?: string;
  domains?: string;
  scopes?: string;
  pingOneRegion?: string;
  pingOneEnvironmentId?: string;
  defaultType?: string;
}

export interface SsoFormValidationInput {
  /** Free-text config name (required). */
  name: string;
  /** OIDC client id (required, non-empty). */
  clientId: string;
  /**
   * Raw secret input value. When it equals the masked literal ("***")
   * we treat it as "unchanged" and skip the non-empty check — the
   * stored secret will be preserved by `buildPatchPayload`.
   */
  clientSecret: string;
  /** `true` when an existing config is being edited (so the stored
   *  secret exists and may be preserved). `false` for the create
   *  flow where a non-empty secret is required up-front. */
  hasExistingSecret: boolean;
  /** OIDC discovery URL. Optional for PingOne when region+envId set. */
  discoveryUrl: string;
  /** Raw comma-separated domains input. */
  domainsRaw: string;
  /** Raw comma-separated scopes input (optional). */
  scopesRaw: string;
  provider: SsoProvider;
  pingOneRegion: string;
  pingOneEnvironmentId: string;
  /** USER_TYPES enum value. Validated upstream in userTypes.ts. */
  defaultType: string;
}

/** Normalize and split a comma-separated input into trimmed non-empty tokens. */
export function parseCommaList(raw: string): string[] {
  if (typeof raw !== 'string') return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function validateDomainsField(raw: string): string | undefined {
  const parts = parseCommaList(raw);
  if (parts.length === 0) {
    return 'At least one email domain is required';
  }
  const bad = parts.find((p) => !DOMAIN_RE.test(p.toLowerCase()));
  if (bad) {
    return `"${bad}" is not a valid domain (e.g. acme.com)`;
  }
  const lower = parts.map((p) => p.toLowerCase());
  if (new Set(lower).size !== lower.length) {
    return 'Duplicate domains are not allowed';
  }
  return undefined;
}

export function validateHttpsUrl(raw: string): string | undefined {
  if (!raw || typeof raw !== 'string' || !raw.trim()) {
    return 'Required';
  }
  let u: URL;
  try {
    u = new URL(raw.trim());
  } catch {
    return 'Must be a valid URL';
  }
  if (u.protocol !== 'https:') {
    return 'Must use https://';
  }
  return undefined;
}

export function validateNonEmpty(raw: string, label: string): string | undefined {
  if (!raw || typeof raw !== 'string' || !raw.trim()) {
    return `${label} is required`;
  }
  return undefined;
}

/**
 * Full form validator. Returns the field-error map (empty when valid)
 * plus a boolean for convenience.
 *
 * Semantics:
 *  - `discoveryUrl` is optional for `provider === 'pingone'` *if* both
 *    `pingOneRegion` and `pingOneEnvironmentId` are supplied — the
 *    auth-service derives the URL in that case (see SF-3404 CREATE +
 *    PATCH handlers).
 *  - `clientSecret` is required only when creating (no existing secret)
 *    OR when the operator has entered a new non-masked value. An
 *    unchanged masked input ("***") is allowed and preserved by the
 *    PATCH payload builder.
 *  - `scopes` are optional; when present every token must be non-empty.
 */
export function validateSsoForm(input: SsoFormValidationInput): {
  errors: FieldErrors;
  isValid: boolean;
} {
  const errors: FieldErrors = {};

  const nameErr = validateNonEmpty(input.name, 'Config name');
  if (nameErr) errors.name = nameErr;

  const clientIdErr = validateNonEmpty(input.clientId, 'Client ID');
  if (clientIdErr) errors.clientId = clientIdErr;

  // Secret: required when creating; when editing, allow the masked literal
  // "***" (unchanged) — anything else non-empty is a new secret.
  const secret = (input.clientSecret ?? '').trim();
  if (!input.hasExistingSecret && !secret) {
    errors.clientSecret = 'Client secret is required for a new config';
  }

  // Discovery URL — optional for pingone+region+envId fallback.
  const hasPingOneFallback =
    input.provider === 'pingone' &&
    !!input.pingOneRegion?.trim() &&
    !!input.pingOneEnvironmentId?.trim();
  if (!hasPingOneFallback) {
    const urlErr = validateHttpsUrl(input.discoveryUrl);
    if (urlErr) errors.discoveryUrl = urlErr;
  } else if (input.discoveryUrl && input.discoveryUrl.trim()) {
    // If given, still validate the shape so we don't ship a typo.
    const urlErr = validateHttpsUrl(input.discoveryUrl);
    if (urlErr) errors.discoveryUrl = urlErr;
  }

  const domErr = validateDomainsField(input.domainsRaw);
  if (domErr) errors.domains = domErr;

  // Scopes — optional, but when supplied must be non-empty tokens only.
  if (input.scopesRaw && input.scopesRaw.trim()) {
    const tokens = parseCommaList(input.scopesRaw);
    if (tokens.length === 0) {
      errors.scopes = 'Scopes must be comma-separated non-empty values';
    }
  }

  // PingOne: if provider is pingone AND discoveryUrl is missing, BOTH
  // region + envId are required (the fallback path). If discoveryUrl is
  // present they're optional metadata.
  if (input.provider === 'pingone' && !input.discoveryUrl?.trim()) {
    if (!input.pingOneRegion?.trim()) {
      errors.pingOneRegion =
        'Region is required when no discovery URL is provided';
    }
    if (!input.pingOneEnvironmentId?.trim()) {
      errors.pingOneEnvironmentId =
        'Environment ID is required when no discovery URL is provided';
    }
  }

  return { errors, isValid: Object.keys(errors).length === 0 };
}
